import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import InsightCard from "@/components/InsightCard";
import AdminChart from "@/components/AdminChart";
import AdminFilters from "@/components/AdminFilters";
import ExportModal from "@/components/ExportModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  Lightbulb,
  MessageSquare,
  ThumbsUp,
  TrendingUp,
  Activity,
  Shield,
  BarChart3,
  PieChart,
  LineChart,
  Calendar,
  CheckCircle,
  XCircle,
  FileText,
  ArrowLeft,
  RefreshCw,
  Download,
  Eye,
  Settings,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import {
  useAdminActivities,
  useActivityStats,
  useDashboardOverview,
  useUserAnalytics,
  useCampaignAnalytics,
  useEngagementAnalytics,
  useConversionFunnel,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { ExportData } from "@/lib/exportUtils";
import UserActivityModal from "@/components/UserActivityModal";
import CampaignActivityModal from "@/components/CampaignActivityModal";
import { Checkbox } from "@/components/ui/checkbox";

const ActivityItem = ({ activity }) => {
  const getActivityIcon = () => {
    switch (activity.type) {
      case "USER_REGISTERED":
        return <Users size={16} className="text-blue-500" />;
      case "CAMPAIGN_CREATED":
        return <FileText size={16} className="text-purple-500" />;
      case "CAMPAIGN_UPDATED":
        return <CheckCircle size={16} className="text-green-600" />;
      case "COMMENT_POSTED":
        return <MessageSquare size={16} className="text-amber-500" />;
      case "RESPONSE_SUBMITTED":
        return <MessageSquare size={16} className="text-blue-500" />;
      case "BLOOM_SCORE_GIVEN":
        return <ThumbsUp size={16} className="text-green-500" />;
      case "MILESTONE_REACHED":
        return <TrendingUp size={16} className="text-purple-500" />;
      case "SIGNIFICANT_ENGAGEMENT":
        return <Activity size={16} className="text-orange-500" />;
      default:
        return <Activity size={16} className="text-gray-500" />;
    }
  };

  const getActivityMessage = () => {
    return (
      <span className="text-sm">
        {activity.description}
        {activity.user && (
          <span className="font-medium text-primary">
            {" "}
            by {activity.user.name}
          </span>
        )}
        {activity.campaign && (
          <span className="font-medium text-primary">
            {" "}
            on {activity.campaign.title}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="flex items-start space-x-2 sm:space-x-3 py-2 sm:py-3 first:pt-0">
      <div className="mt-0.5">
        <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-slate-100 flex items-center justify-center">
          {getActivityIcon()}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs sm:text-sm">{getActivityMessage()}</div>
        <p className="text-xs text-muted-foreground mt-1">
          {new Date(activity.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    timeRange: "all",
    userType: "all",
    projectStatus: "all",
    searchTerm: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);

  const handleCampaignSelection = (campaignId: string, checked: boolean) => {
    setSelectedCampaigns((prev) =>
      checked ? [...prev, campaignId] : prev.filter((id) => id !== campaignId)
    );
  };

  // Debug logging
  console.log(
    "🔍 [AdminDashboard] Component rendered - isAuthenticated:",
    isAuthenticated,
    "user role:",
    user?.role
  );

  // Fetch analytics data with better error handling
  const {
    data: dashboardOverview,
    isLoading: overviewLoading,
    error: overviewError,
    isError: overviewIsError,
  } = useDashboardOverview();

  const {
    data: userAnalytics,
    isLoading: userLoading,
    error: userError,
    isError: userIsError,
  } = useUserAnalytics();

  const {
    data: campaignAnalytics,
    isLoading: campaignLoading,
    error: campaignError,
    isError: campaignIsError,
  } = useCampaignAnalytics();

  const {
    data: engagementAnalytics,
    isLoading: engagementLoading,
    error: engagementError,
    isError: engagementIsError,
  } = useEngagementAnalytics();

  const {
    data: conversionFunnel,
    isLoading: funnelLoading,
    error: funnelError,
    isError: funnelIsError,
  } = useConversionFunnel();

  // Activity hooks
  const { data: adminActivities, isLoading: activitiesLoading } =
    useAdminActivities();
  const { data: activityStats, isLoading: statsLoading } = useActivityStats();

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.timeRange !== "all") count++;
    if (filters.userType !== "all") count++;
    if (filters.projectStatus !== "all") count++;
    if (filters.searchTerm) count++;
    return count;
  }, [filters]);

  // Chart configurations with real data
  const userGrowthConfig = {
    newUsers: { color: "#6366F1", label: "New Users" },
    totalUsers: { color: "#10B981", label: "Total Users" },
  };

  const userGrowthChartData = useMemo(() => {
    console.log(
      "🔍 [AdminDashboard] Processing user growth data:",
      userAnalytics?.growthData
    );

    if (
      !userAnalytics?.growthData ||
      !Array.isArray(userAnalytics.growthData)
    ) {
      console.log("❌ [AdminDashboard] No user growth data available");
      return [];
    }

    const processedData = userAnalytics.growthData
      .filter((item) => item && item.date) // Filter out invalid items
      .map((item) => {
        const newUsers = Number(item.newUsers) || 0;
        const totalUsers = Number(item.totalUsers) || 0;

        console.log(
          `📊 [AdminDashboard] Date: ${item.date}, New Users: ${newUsers}, Total Users: ${totalUsers}`
        );

        return {
          name: new Date(item.date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          newUsers,
          totalUsers,
        };
      });

    console.log(
      "✅ [AdminDashboard] Processed user growth data:",
      processedData
    );
    return processedData;
  }, [userAnalytics?.growthData]);

  const projectStatusConfig = {
    validated: { color: "#10B981", label: "Validated" },
    active: { color: "#6366F1", label: "Active" },
    draft: { color: "#6B7280", label: "Draft" },
    archived: { color: "#EF4444", label: "Archived" },
  };

  // FIXED: Project Status Chart Data with proper validation
  const projectStatusChartData = useMemo(() => {
    if (
      !campaignAnalytics?.statusDistribution ||
      !Array.isArray(campaignAnalytics.statusDistribution)
    ) {
      return [];
    }

    return campaignAnalytics.statusDistribution
      .filter(
        (status) =>
          status && status.status && typeof status.count !== "undefined"
      )
      .map((status) => ({
        name: status.status,
        value: Number(status.count) || 0, // Ensure it's a number
        color:
          status.status === "ACTIVE"
            ? "#6366F1"
            : status.status === "DRAFT"
            ? "#6B7280"
            : status.status === "ARCHIVED"
            ? "#EF4444"
            : "#10B981",
      }));
  }, [campaignAnalytics?.statusDistribution]);

  const engagementConfig = {
    responses: { color: "#F59E0B", label: "Responses" },
    comments: { color: "#EF4444", label: "Comments" },
    likes: { color: "#8B5CF6", label: "Likes" },
  };

  // FIXED: Engagement Chart Data with proper validation
  const engagementChartData = useMemo(() => {
    if (
      !engagementAnalytics?.responseTrends ||
      !Array.isArray(engagementAnalytics.responseTrends)
    ) {
      return [];
    }

    return engagementAnalytics.responseTrends
      .slice(-7) // Get last 7 items
      .filter((item) => item && item.date) // Filter out invalid items
      .map((item) => ({
        name: new Date(item.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        responses: Number(item.responses) || 0, // Ensure it's a number
        comments: Number(item.comments) || 0, // Ensure it's a number
        likes: Number(item.likes) || 0, // Ensure it's a number
      }));
  }, [engagementAnalytics?.responseTrends]);

  const conversionConfig = {
    users: { color: "#10B981", label: "Users" },
    conversionRate: { color: "#6366F1", label: "Conversion Rate" },
  };

  // FIXED: Conversion Chart Data with proper validation
  const conversionChartData = useMemo(() => {
    if (!conversionFunnel?.stages || !Array.isArray(conversionFunnel.stages)) {
      return [];
    }

    return conversionFunnel.stages
      .filter(
        (stage) => stage && stage.stage && typeof stage.count !== "undefined"
      )
      .map((stage) => ({
        name: stage.stage,
        users: Number(stage.count) || 0, // Ensure it's a number
        conversionRate: Number(stage.conversionRate) || 0, // Ensure it's a number
      }));
  }, [conversionFunnel?.stages]);

  // Filter real data based on current filters - with stable fallbacks
  const filteredUsers = useMemo(() => {
    if (!userAnalytics?.topUsers) return [];
    return userAnalytics.topUsers.filter((user) => {
      if (
        filters.searchTerm &&
        !user.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) &&
        !user.email.toLowerCase().includes(filters.searchTerm.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [userAnalytics?.topUsers, filters.searchTerm]);

  const filteredCampaigns = useMemo(() => {
    if (!campaignAnalytics?.performanceMetrics) return [];
    return campaignAnalytics.performanceMetrics.filter((campaign) => {
      if (
        filters.searchTerm &&
        !campaign.title
          .toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) &&
        !campaign.creator
          .toLowerCase()
          .includes(filters.searchTerm.toLowerCase())
      ) {
        return false;
      }
      if (
        filters.projectStatus !== "all" &&
        campaign.status !== filters.projectStatus
      ) {
        return false;
      }
      return true;
    });
  }, [
    campaignAnalytics?.performanceMetrics,
    filters.searchTerm,
    filters.projectStatus,
  ]);

  const handleActionClick = (action, item) => {
    if (action === "Managed" && item.name) {
      // User action
      setSelectedUser(item);
      setIsUserModalOpen(true);
    } else if (action === "Reviewed" && item.title) {
      // Campaign action
      setSelectedCampaign(item);
      setIsCampaignModalOpen(true);
    } else {
      toast({
        title: `${action} ${item.title || item.name}`,
        description: `Action triggered for ${item.title || item.name}`,
      });
    }
  };

  // Debug logging for data
  console.log("🔍 [AdminDashboard] Dashboard overview:", dashboardOverview);
  console.log("🔍 [AdminDashboard] User analytics:", userAnalytics);
  console.log("🔍 [AdminDashboard] Campaign analytics:", campaignAnalytics);
  console.log("🔍 [AdminDashboard] Engagement analytics:", engagementAnalytics);
  console.log("🔍 [AdminDashboard] Conversion funnel:", conversionFunnel);

  // Debug logging for errors
  if (overviewError)
    console.error("❌ [AdminDashboard] Overview error:", overviewError);
  if (userError)
    console.error("❌ [AdminDashboard] User analytics error:", userError);
  if (campaignError)
    console.error(
      "❌ [AdminDashboard] Campaign analytics error:",
      campaignError
    );
  if (engagementError)
    console.error(
      "❌ [AdminDashboard] Engagement analytics error:",
      engagementError
    );
  if (funnelError)
    console.error("❌ [AdminDashboard] Conversion funnel error:", funnelError);

  // Check if user is admin - with more stable check
  const isAdmin = isAuthenticated && user?.role === "ADMIN";

  if (!isAuthenticated) {
    console.log("❌ [AdminDashboard] Access denied - not authenticated");
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                Please log in to access the admin dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    console.log("❌ [AdminDashboard] Access denied - not admin user");
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                You need admin privileges to access this dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - only show if we're still loading and don't have any data
  const isLoading =
    (overviewLoading || userLoading || campaignLoading) &&
    !dashboardOverview &&
    !userAnalytics &&
    !campaignAnalytics;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16 max-w-7xl">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 sm:w-12 sm:h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-sm sm:text-base text-slate-600">
                Loading admin dashboard...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state - show partial data if some queries failed
  const hasErrors =
    overviewIsError ||
    userIsError ||
    campaignIsError ||
    engagementIsError ||
    funnelIsError;

  if (hasErrors) {
    console.warn(
      "⚠️ [AdminDashboard] Some analytics queries failed, showing partial data"
    );
  }

  // Prepare export data
  const exportData: ExportData = {
    users: filteredUsers.map((user) => ({
      name: user.name,
      email: user.email,
      campaigns: user.campaignsCreated,
      feedbackGiven: user.responsesGiven,
      totalEngagement: user.totalEngagement,
      joined: new Date(user.joinedDate).toLocaleDateString(),
      status: "Active", // Default status since it's not in the data
    })),
    campaigns: filteredCampaigns.map((campaign) => ({
      title: campaign.title,
      creator: campaign.creator,
      status: campaign.status,
      responses: campaign.responses,
      comments: campaign.comments,
      created: new Date().toLocaleDateString(), // Default since created date not available
      category: "General", // Default category since it's not in the data
    })),
    analytics: {
      userGrowth: userAnalytics?.growthData || [],
      campaignGrowth: campaignAnalytics?.campaignGrowth || [],
      engagement: engagementAnalytics?.responseTrends || [],
    },
    activity:
      adminActivities?.map((activity) => ({
        type: activity.type,
        description: activity.description,
        user: activity.user?.name || "Unknown",
        campaign: activity.campaign?.title || "N/A",
        createdAt: new Date(activity.createdAt).toLocaleDateString(),
      })) || [],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-primary/5">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16 max-w-7xl">
        {/* Back Button */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-r from-primary to-accent">
                <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Real-time insights and analytics for your platform
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <ExportModal
                data={exportData}
                filters={filters}
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs sm:text-sm"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Export Report</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                }
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/users")}
                className="text-xs sm:text-sm"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Manage Users</span>
                <span className="sm:hidden">Users</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/settings")}
                className="text-xs sm:text-sm"
              >
                <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
                <span className="sm:hidden">Settings</span>
              </Button>
            </div>
          </div>

          {/* Filters */}
          <AdminFilters
            filters={filters}
            onFiltersChange={setFilters}
            activeFiltersCount={activeFiltersCount}
          />
        </motion.div>

        <div className="space-y-6 sm:space-y-8">
          {/* Stats Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            <InsightCard
              title="Total Users"
              value={(
                dashboardOverview?.userStats?.total || 0
              ).toLocaleString()}
              description={`${
                dashboardOverview?.userStats?.newThisMonth || 0
              } new this month`}
              trend="up"
              trendValue={`${dashboardOverview?.userStats?.growthRate || 0}%`}
              icon={<Users size={18} />}
              accentColor="blue"
              className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm"
            />
            <InsightCard
              title="Active Campaigns"
              value={(
                dashboardOverview?.campaignStats?.active || 0
              ).toLocaleString()}
              description={`${
                dashboardOverview?.campaignStats?.validated || 0
              } validated`}
              trend="up"
              trendValue="8.3%"
              icon={<Lightbulb size={18} />}
              accentColor="green"
              className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm"
            />
            <InsightCard
              title="Total Feedback"
              value={(
                dashboardOverview?.engagementStats?.totalResponses || 0
              ).toLocaleString()}
              description={`${
                dashboardOverview?.engagementStats?.avgEngagementPerUser || 0
              }/user avg`}
              trend="up"
              trendValue="15.2%"
              icon={<MessageSquare size={18} />}
              accentColor="accent"
              className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm"
            />
            <InsightCard
              title="Response Rate"
              value={`${
                dashboardOverview?.engagementStats?.responseRate || 0
              }%`}
              description={`${
                dashboardOverview?.engagementStats?.avgEngagementPerUser || 0
              } avg engagement`}
              trend="up"
              trendValue="2.8%"
              icon={<ThumbsUp size={18} />}
              accentColor="primary"
              className="border-0 shadow-lg bg-gradient-to-br from-card/50 to-card/30 backdrop-blur-sm"
            />
          </motion.div>

          {/* Enhanced Charts Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6"
          >
            <AdminChart
              title="User Growth Trends"
              description="New user registrations and total growth over time"
              icon={<TrendingUp className="h-5 w-5" />}
              type="area"
              data={userGrowthChartData.length > 0 ? userGrowthChartData : []}
              config={userGrowthConfig}
              height={320}
            />

            <AdminChart
              title="Campaign Status Distribution"
              description="Current breakdown of all campaigns by status"
              icon={<PieChart className="h-5 w-5" />}
              type="pie"
              data={
                projectStatusChartData.length > 0 ? projectStatusChartData : []
              }
              config={projectStatusConfig}
              height={320}
              showGrid={false}
            />

            <AdminChart
              title="Weekly Engagement"
              description="User interactions across the platform this week"
              icon={<BarChart3 className="h-5 w-5" />}
              type="bar"
              data={engagementChartData.length > 0 ? engagementChartData : []}
              config={engagementConfig}
              height={320}
            />

            <AdminChart
              title="Conversion Funnel"
              description="From sign-ups to campaign creation and validation"
              icon={<LineChart className="h-5 w-5" />}
              type="line"
              data={conversionChartData.length > 0 ? conversionChartData : []}
              config={conversionConfig}
              height={320}
            />
          </motion.div>

          {/* Main Content Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-muted/50 to-transparent border-b border-border/50 pb-0">
                <Tabs defaultValue="users" className="w-full">
                  <TabsList className="w-full justify-start mb-4 sm:mb-6 bg-card/50 backdrop-blur-sm border border-border/50 h-10 sm:h-12 overflow-x-auto">
                    <TabsTrigger
                      value="users"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-accent data-[state=active]:text-white flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                    >
                      <Users size={14} className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Users</span>
                      <span className="sm:hidden">Users</span>
                      <span className="hidden sm:inline">
                        ({filteredUsers.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="campaigns"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-accent data-[state=active]:to-primary data-[state=active]:text-white flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                    >
                      <Lightbulb size={14} className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Campaigns</span>
                      <span className="sm:hidden">Campaigns</span>
                      <span className="hidden sm:inline">
                        ({filteredCampaigns.length})
                      </span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="activity"
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-blue-500 data-[state=active]:text-white flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                    >
                      <Activity size={14} className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Recent Activity</span>
                      <span className="sm:hidden">Activity</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Users Tab */}
                  <TabsContent
                    value="users"
                    className="mt-0 space-y-4 sm:space-y-6 p-0"
                  >
                    <div className="rounded-lg border border-border/50 overflow-hidden bg-background/30 overflow-x-auto">
                      <Table>
                        <TableHeader className="bg-muted/30">
                          <TableRow>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              User
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm hidden sm:table-cell">
                              Email
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              Campaigns
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm hidden sm:table-cell">
                              Feedback
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm hidden lg:table-cell">
                              Engagement
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm hidden lg:table-cell">
                              Joined
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(filteredUsers || [])
                            .slice(0, 10)
                            .map((user, index) => (
                              <motion.tr
                                key={user.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="hover:bg-muted/20 transition-colors"
                              >
                                <TableCell className="font-medium text-xs sm:text-sm">
                                  <div>
                                    <div className="font-semibold">
                                      {user.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground sm:hidden">
                                      {user.email}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-xs sm:text-sm hidden sm:table-cell">
                                  {user.email}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className="bg-primary/10 text-primary border-primary/20 text-xs"
                                  >
                                    {user.campaignsCreated}
                                  </Badge>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">
                                  <Badge
                                    variant="outline"
                                    className="bg-accent/10 text-accent border-accent/20 text-xs"
                                  >
                                    {user.responsesGiven}
                                  </Badge>
                                </TableCell>
                                <TableCell className="hidden lg:table-cell">
                                  <Badge
                                    variant="outline"
                                    className="bg-green-500/10 text-green-600 border-green-500/20 text-xs"
                                  >
                                    {user.totalEngagement}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-xs sm:text-sm hidden lg:table-cell">
                                  {new Date(
                                    user.joinedDate
                                  ).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleActionClick("Managed", user)
                                    }
                                    className="hover:bg-primary/10 text-xs sm:text-sm"
                                  >
                                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                    <span className="hidden sm:inline">
                                      View
                                    </span>
                                    <span className="sm:hidden">View</span>
                                  </Button>
                                </TableCell>
                              </motion.tr>
                            ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="flex justify-center pb-4 sm:pb-6">
                      <Button
                        variant="outline"
                        onClick={() => navigate("/admin/users")}
                        className="bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 text-xs sm:text-sm"
                      >
                        <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">View All Users</span>
                        <span className="sm:hidden">View All</span>
                        <span className="hidden sm:inline">
                          ({filteredUsers.length})
                        </span>
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Campaigns Tab */}
                  <TabsContent
                    value="campaigns"
                    className="mt-0 space-y-4 sm:space-y-6 p-0"
                  >
                    <div className="rounded-lg border border-border/50 overflow-hidden bg-background/30 overflow-x-auto">
                      <Table>
                        <TableHeader className="bg-muted/30">
                          <TableRow>
                            <TableHead className="font-semibold w-8 sm:w-12 text-xs sm:text-sm"></TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              Campaign
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm hidden sm:table-cell">
                              Creator
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              Responses
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm hidden lg:table-cell">
                              Engagement
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              Status
                            </TableHead>
                            <TableHead className="font-semibold text-xs sm:text-sm">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(filteredCampaigns || [])
                            .slice(0, 10)
                            .map((campaign, index) => (
                              <motion.tr
                                key={campaign.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="hover:bg-muted/20 transition-colors"
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={selectedCampaigns.includes(
                                      campaign.id
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleCampaignSelection(
                                        campaign.id,
                                        checked as boolean
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell className="font-medium text-xs sm:text-sm">
                                  <div>
                                    <div className="font-semibold">
                                      {campaign.title}
                                    </div>
                                    <div className="text-xs sm:text-sm text-muted-foreground">
                                      <span className="sm:hidden">
                                        {campaign.creator} •{" "}
                                      </span>
                                      {campaign.daysActive} days active
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-xs sm:text-sm hidden sm:table-cell">
                                  {campaign.creator}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-xs"
                                  >
                                    {campaign.responses}
                                  </Badge>
                                </TableCell>
                                <TableCell className="hidden lg:table-cell">
                                  <Badge
                                    variant="outline"
                                    className="bg-purple-500/10 text-purple-600 border-purple-500/20 text-xs"
                                  >
                                    {campaign.engagementRate}%
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      campaign.status === "ACTIVE"
                                        ? "default"
                                        : campaign.status === "ARCHIVED"
                                        ? "secondary"
                                        : "outline"
                                    }
                                    className="text-xs"
                                  >
                                    {campaign.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleActionClick("Reviewed", campaign)
                                    }
                                    className="hover:bg-accent/10 text-xs sm:text-sm"
                                  >
                                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                    <span className="hidden sm:inline">
                                      View
                                    </span>
                                    <span className="sm:hidden">View</span>
                                  </Button>
                                </TableCell>
                              </motion.tr>
                            ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="flex justify-center pb-4 sm:pb-6">
                      <Button
                        variant="outline"
                        onClick={() => navigate("/admin/moderation")}
                        className="bg-accent/10 hover:bg-accent/20 text-accent border-accent/20 text-xs sm:text-sm"
                      >
                        <Lightbulb className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">
                          View All Campaigns
                        </span>
                        <span className="sm:hidden">View All</span>
                        <span className="hidden sm:inline">
                          ({filteredCampaigns.length})
                        </span>
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Activity Tab */}
                  <TabsContent value="activity" className="mt-0 p-4 sm:p-6">
                    <div className="space-y-3 sm:space-y-4">
                      {activitiesLoading ? (
                        <div className="text-center py-6 sm:py-8">
                          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary mx-auto mb-3 sm:mb-4"></div>
                          <p className="text-sm sm:text-base text-muted-foreground">
                            Loading activities...
                          </p>
                        </div>
                      ) : (adminActivities || []).length > 0 ? (
                        (adminActivities || [])
                          .slice(0, 10)
                          .map((activity, index) => (
                            <motion.div
                              key={activity.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                            >
                              <ActivityItem activity={activity} />
                            </motion.div>
                          ))
                      ) : (
                        <div className="text-center py-6 sm:py-8">
                          <Activity className="w-8 h-8 sm:w-12 sm:h-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
                          <p className="text-sm sm:text-base text-muted-foreground">
                            No recent activities
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-center pb-4 sm:pb-6">
                      <Button
                        className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 border-purple-500/20 text-xs sm:text-sm"
                        variant="outline"
                        onClick={() => navigate("/admin/activity")}
                      >
                        <Activity className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">
                          View All Activities
                        </span>
                        <span className="sm:hidden">View All</span>
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardHeader>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* User Activity Modal */}
      <UserActivityModal
        user={selectedUser}
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedUser(null);
        }}
      />

      {/* Campaign Activity Modal */}
      <CampaignActivityModal
        campaign={selectedCampaign}
        isOpen={isCampaignModalOpen}
        onClose={() => {
          setIsCampaignModalOpen(false);
          setSelectedCampaign(null);
        }}
      />
    </div>
  );
};

export default AdminDashboard;
